import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { isCustomerWithExistingVehiclePolicy } from './shared/vehicle';

type checkboxesValueType = [{ label: string; value: string }];

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    const whatDamageDuringTheft: checkboxesValueType = getValueOrDefault(
        formConfig,
        formValues,
        'whatDamageOccurDuringTheft'
    );
    const whatDamageDuringCarTheft: checkboxesValueType = getValueOrDefault(
        formConfig,
        formValues,
        'whatDamageOccurDuringCarTheft'
    );

    const isInMyCar = getValueOrDefault(formConfig, formValues, 'lossLocation').value === 'showInMyVehicle';
    const isOtherPlace = getValueOrDefault(formConfig, formValues, 'lossLocation').value === 'showOtherPlace';
    switch (curPageId) {
        case 'godt-at-vide':
            nextPage = 'dato';
            break;

        case 'dato':
            nextPage = 'adresse';
            break;

        case 'adresse':
            if (isOtherPlace) {
                nextPage = 'om-tyveriet';
            } else {
                nextPage = 'om-vinduer-og-doere';
            }
            break;

        case 'om-tyveriet':
            nextPage = 'beskriv-skaden';
            break;

        case 'om-vinduer-og-doere':
            if (isInMyCar) {
                nextPage = 'tegn-paa-indbrud';
            } else {
                nextPage = 'om-beboelse';
            }
            break;

        case 'tegn-paa-indbrud':
            nextPage = 'beskriv-skaden';
            break;

        case 'om-beboelse':
            nextPage = 'beskriv-skaden';
            break;

        case 'beskriv-skaden':
            if (isOtherPlace) {
                nextPage = 'registrer-nu-eller-senere';
            } else if (isInMyCar) {
                nextPage = 'hvilke-skader-2';
            } else {
                nextPage = 'hvilke-skader-1';
            }
            break;

        case 'registrer-nu-eller-senere':
            if (getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes') {
                nextPage = 'genstandsregistrering';
            } else {
                nextPage = getNextPageBasedOnCheckbox(
                    isInMyCar,
                    whatDamageDuringCarTheft,
                    isOtherPlace,
                    whatDamageDuringTheft
                );
            }
            break;

        case 'genstandsregistrering':
            nextPage = getNextPageBasedOnCheckbox(
                isInMyCar,
                whatDamageDuringCarTheft,
                isOtherPlace,
                whatDamageDuringTheft
            );
            break;

        case 'hvilke-skader-1':
            if (whatDamageDuringTheft.some((damage) => damage.value === 'wereItemsStolen')) {
                nextPage = 'registrer-nu-eller-senere';
            } else {
                nextPage = 'reparation-af-skaden';
            }
            break;

        case 'hvilke-skader-2':
            if (whatDamageDuringCarTheft.some((damage) => damage.value === 'wereItemsStolen')) {
                nextPage = 'registrer-nu-eller-senere';
            } else {
                nextPage = 'vaelg-koeretoej';
            }
            break;

        case 'reparation-af-skaden':
            const whoRepairsDamage = getValueOrDefault(formConfig, formValues, 'whoRepairsDamage').value;
            if (whoRepairsDamage === 'other' || whoRepairsDamage === 'customer') {
                nextPage = 'pris-paa-reparation';
            } else {
                nextPage = 'dokumentation';
            }
            break;

        case 'pris-paa-reparation':
            nextPage = 'dokumentation';
            break;

        case 'vaelg-koeretoej':
            const registratioNumberField = getValueOrDefault(formConfig, formValues, 'registrationNumber');
            const { registrationNumber } = registratioNumberField;

            // If no registationNumber.registrationNumber always to andet-koeretoej-nummerplade
            if (registrationNumber === '') {
                nextPage = 'andet-koeretoej-nummerplade';
            } else {
                nextPage = 'skadezoner';
            }
            break;

        case 'andet-koeretoej-nummerplade':
            nextPage = 'om-andet-koeretoej';
            break;

        case 'om-andet-koeretoej':
            nextPage = 'skadezoner';

            // skip the 'skadezoner' page if the user selected 'other' as vehicle type
            // user can select the 'other' option in two different ways, depending on if the customer has a policy or not
            const registrationNumberElement = getValueOrDefault(formConfig, formValues, 'registrationNumber');
            const isOtherVehicleSelected = isCustomerWithExistingVehiclePolicy(formConfig, formValues)
                ? registrationNumberElement.entityType === 'others' &&
                  getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other'
                : registrationNumberElement.entityType === 'others';

            if (isOtherVehicleSelected) {
                nextPage = 'skader-andet-koeretoej';
            }

            break;

        case 'skadezoner':
            nextPage = 'politi-og-samtykke';
            break;
        case 'skader-andet-koeretoej':
            nextPage = 'politi-og-samtykke';
            break;

        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else {
                nextPage = 'dokumentation';
            }
            break;

        case 'samtykke':
            nextPage = 'dokumentation';
            break;

        case 'dokumentation':
            nextPage = 'om-dig';
            break;

        case 'om-dig':
            nextPage = 'resume';
            break;

        case 'resume':
            nextPage = undefined;
            break;

        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

const getNextPageBasedOnCheckbox = (
    isInMyCar: boolean,
    whatDamageDuringCarTheft: checkboxesValueType,
    isOtherPlace: boolean,
    whatDamageDuringTheft: checkboxesValueType
) => {
    let nextPage = 'dokumentation';
    if (isInMyCar && whatDamageDuringCarTheft.some((damage) => damage.value === 'hasVehicleDamages')) {
        nextPage = 'vaelg-koeretoej';
    } else if (!isOtherPlace && whatDamageDuringTheft.some((damage) => damage.value === 'hasBuildingDamages')) {
        nextPage = 'reparation-af-skaden';
    }
    return nextPage;
};
